import type { Notification } from '@backstage/plugin-notifications-common';

/** Browsers show at most two buttons (Chrome on Windows/Linux); extra ones are dropped. */
const MAX_ACTIONS = 2;

export interface WebNotificationAction {
  title: string;
  /** Absolute URL, opened when the button is clicked. */
  link: string;
}

const isText = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/** Reads `metadata.actions` set by the backend notifiers, ignoring anything malformed. */
export function parseActions(metadata: Notification['payload']['metadata'], origin: string): WebNotificationAction[] {
  const raw = metadata?.actions;
  if (!Array.isArray(raw)) return [];
  const actions: WebNotificationAction[] = [];
  for (const item of raw) {
    if (actions.length === MAX_ACTIONS) break;
    if (typeof item !== 'object' || item === null || Array.isArray(item)) continue;
    const { title, link } = item;
    if (!isText(title) || !isText(link)) continue;
    try {
      const url = new URL(link, origin);
      if (url.protocol === 'http:' || url.protocol === 'https:') actions.push({ title, link: url.href });
    } catch {
      // not a URL: no button
    }
  }
  return actions;
}

export interface WebNotificationContent {
  title: string;
  options: NotificationOptions & { actions?: { action: string; title: string }[] };
}

/** What to hand to `registration.showNotification`. `data` comes back in the worker's click event. */
export function toWebNotification(notification: Notification, origin: string): WebNotificationContent {
  const { payload } = notification;
  const actions = parseActions(payload.metadata, origin);
  return {
    title: payload.title,
    options: {
      body: payload.description ?? '',
      tag: notification.id, // one notification per id, even with several tabs open
      icon: '/android-chrome-192x192.png',
      badge: '/favicon-32x32.png',
      requireInteraction: payload.severity === 'critical',
      actions: actions.map((a, i) => ({ action: `action-${i}`, title: a.title })),
      data: {
        id: notification.id,
        link: payload.link ? new URL(payload.link, origin).href : new URL('/notifications', origin).href,
        actions,
      },
    },
  };
}
